import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import React from 'react';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { LoaderContainer } from 'components/common';
import NoData from 'components/common/NoData/noData';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import BaselineVsActual from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/BaselineVsActual/BaselineVsActual';
import { useProgressSummaryProvider } from 'components/widgets/ProgressSummary/ProgressSummary.provider';
import { useBaseWidget } from 'hooks/useBaseWidget';
import { useProgressSummaryLogic } from 'components/widgets/ProgressSummary/ProgressSummary.logic';

const id = 'BaselineVsActual';
const title = 'Progress Summary';
const componentKey = 'progressSummary';

const ProgressSummary = ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: IComponentProps) => {
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    const { ui, api } = useProgressSummaryProvider({
        componentKey,
        widgetId,
        globalFilterData,
        localFilters,
        contractId,
        latestVersionId,
        compareVersionId,
        projectId,
    });

    const { logic } = useProgressSummaryLogic({ ui, api });

    return (
        <ErrorBoundary>
            <WidgetWithTitle
                id={id}
                title={title}
                tooltip={api.getBaselineVsActual.data?.info}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                    />,
                    <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                    <ExportDropdown key={'export'} title={title} subTitle={subTitle} />,
                ]}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && ui.pills.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                    ) : null,
                ]}
            >
                {api.getBaselineVsActual.data && !api.getBaselineVsActual.isLoading && !logic.isNoData && (
                    <BaselineVsActual
                        data={api.getBaselineVsActual.data}
                        loading={api.getBaselineVsActual.isLoading}
                        progressWeighted={ui.state.progressMeasurement}
                        milestoneFragnetTaskVersionHashCode={logic.milestoneFragnetTaskVersionHashCode}
                    />
                )}
                {api.getBaselineVsActual.isLoading && !api.getBaselineVsActual.data && logic.isNoData && (
                    <LoaderContainer>
                        <OverlayWithSpinner />
                    </LoaderContainer>
                )}
                {!api.getBaselineVsActual.isLoading && logic.isNoData && (
                    <NoData>{logic.notApplicable ? 'Not applicable with applied filter' : null}</NoData>
                )}
            </WidgetWithTitle>
        </ErrorBoundary>
    );
};
export default ProgressSummary;
